import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState, FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import { CardListItem, CardListItemProps } from 'record-it-components/cardList/CardListItem/CardListItem';
import { CardListItemIconButton } from 'record-it-components/cardList/CardListItemIconButton/CardListItemIconButton';
import { CardListItemTitle } from 'record-it-components/cardList/CardListItemTitle/CardListItemTitle';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { ClockTick } from 'record-it-utils/utils/ClockTick/ClockTick';
import { DurationUtils } from 'record-it-utils/utils/DurationUtils/DurationUtils';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { RoutePaths } from '../../../classes/RoutePaths/RoutePaths';
import { ImmutableStopwatchInfo } from '../../../classes/timeTracking/ImmutableStopwatch/ImmutableStopwatch';
import { StopwatchWorkDayEvaluator } from '../../../classes/timeTracking/StopwatchWorkDayEvaluator/StopwatchWorkDayEvaluator';
import { useStopwatchManager } from '../../../hooks/stopwatch/useStopwatchManager';
import { useAppDispatch } from '../../../store/hooks';
import { createWorkTimeEntrySlice } from '../../../store/slice/createWorkTimeEntry/createWorkTimeEntrySlice';
export type StopwatchListItemProps = PropsWithoutChildren<CardListItemProps> & {
  stopwatchInfo: ImmutableStopwatchInfo;
};
export const StopwatchListItem: FunctionComponent<StopwatchListItemProps> = React.memo(props => {
  const $ = _c(39);
  let remainingProps;
  let stopwatchInfo;
  let t0;
  let t1;
  if ($[0] !== props) {
    ({
      stopwatchInfo,
      ...remainingProps
    } = prepareProps({
      props,
      ignoreChildren: true
    }));
    t0 = useState;
    t1 = stopwatchInfo.getDuration();
    $[0] = props;
    $[1] = remainingProps;
    $[2] = stopwatchInfo;
    $[3] = t0;
    $[4] = t1;
  } else {
    remainingProps = $[1];
    stopwatchInfo = $[2];
    t0 = $[3];
    t1 = $[4];
  }
  const [renderedDuration, setRenderedDuration] = t0(t1);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  let t2;
  let t3;
  if ($[5] !== stopwatchInfo) {
    t2 = () => {
      setRenderedDuration(stopwatchInfo.getDuration());
      const disposable = ClockTick.onSecondTick(() => {
        setRenderedDuration(stopwatchInfo.getDuration());
      });
      return () => disposable.dispose();
    };
    t3 = [stopwatchInfo, setRenderedDuration];
    $[5] = stopwatchInfo;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  const manager = useStopwatchManager();
  let t4;
  if ($[8] !== manager || $[9] !== stopwatchInfo) {
    t4 = () => {
      manager.remove(stopwatchInfo.getId());
    };
    $[8] = manager;
    $[9] = stopwatchInfo;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleDeleteClick = useEventCallback(t4);
  let t5;
  if ($[11] !== manager || $[12] !== stopwatchInfo) {
    t5 = () => {
      manager.pause(stopwatchInfo.getId());
    };
    $[11] = manager;
    $[12] = stopwatchInfo;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handlePauseClick = useEventCallback(t5);
  let t6;
  if ($[14] !== manager || $[15] !== stopwatchInfo) {
    t6 = () => {
      manager.start(stopwatchInfo.getId());
    };
    $[14] = manager;
    $[15] = stopwatchInfo;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const handleStartClick = useEventCallback(t6);
  let t7;
  if ($[17] !== dispatch || $[18] !== navigate || $[19] !== stopwatchInfo) {
    t7 = () => {
      const evaluator = new StopwatchWorkDayEvaluator();
      const data = evaluator.evaluate(stopwatchInfo);
      dispatch(createWorkTimeEntrySlice.actions.startCreation({
        startDate: data.startDate.toISOString(),
        endDate: data.endDate.toISOString(),
        breaks: data.breaks,
        note: "",
        userGroupId: stopwatchInfo.getUserGroupId(),
        stopwatchId: stopwatchInfo.getId()
      }));
      navigate(RoutePaths.createWorkTimeEntry());
    };
    $[17] = dispatch;
    $[18] = navigate;
    $[19] = stopwatchInfo;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const handleCreateClick = useEventCallback(t7);
  let t8;
  if ($[21] !== renderedDuration) {
    t8 = DurationUtils.formatDurationToHourMinuteSecondString(renderedDuration);
    $[21] = renderedDuration;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== t8) {
    t9 = <CardListItemTitle>{t8}</CardListItemTitle>;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== handlePauseClick || $[26] !== handleStartClick || $[27] !== stopwatchInfo) {
    t10 = stopwatchInfo.isStarted() ? <CardListItemIconButton {...iconConfigurations.prefixed.stopwatch.pause} onClick={handlePauseClick} data-testid="StopwatchListItem.PauseButton" /> : <CardListItemIconButton {...iconConfigurations.prefixed.stopwatch.play} onClick={handleStartClick} data-testid="StopwatchListItem.PlayButton" />;
    $[25] = handlePauseClick;
    $[26] = handleStartClick;
    $[27] = stopwatchInfo;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== handleDeleteClick) {
    t11 = <CardListItemIconButton {...iconConfigurations.prefixed.listItem.deleteIcon} onClick={handleDeleteClick} data-testid="StopwatchListItem.DeleteButton" />;
    $[29] = handleDeleteClick;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== handleCreateClick) {
    t12 = <CardListItemIconButton {...iconConfigurations.prefixed.listItem.createFromListItem} onClick={handleCreateClick} data-testid="StopwatchListItem.CreateButton" />;
    $[31] = handleCreateClick;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== remainingProps || $[34] !== t10 || $[35] !== t11 || $[36] !== t12 || $[37] !== t9) {
    t13 = <CardListItem {...remainingProps}>{t9}{t10}{t11}{t12}</CardListItem>;
    $[33] = remainingProps;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
    $[37] = t9;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  return t13;
});